import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  setFilter,
  fetchRegionsAPI,
  fetchPlannerEinAPI,
  setSubmit,
  fetchFiltersAPI
} from "./AdminFilter.util";
import { BreadCrumbContext } from '../common/BreadCrumbContext';

const AdminFilter = ({ setSnackopen, setSnackmsg, setLoading, params }) => {
  const [filters, setFilters] = useState({});
  const [excludeSolutions, setExcludeSolutions] = useState([]);
  const [region, setRegion] = useState("");
  const [regionOptions, setRegionOptions] = useState([]);
  const [plannerEIN, setPlannerEIN] = useState("");
  const [plannerEINOptions, setPlannerEINOptions] = useState([]);
  const [snackSuccess, setSnackSuccess] = useState(false);
  const [successAlert, setSuccessAlert] = useState(false);

  const {setStopRefresh} = useContext(BreadCrumbContext);
  
  
  const handleClose = () => {
    setSuccessAlert(false);
  };
    useEffect(() => {
      const loadFilters = async () => {
        const response = await fetchFiltersAPI(setSnackmsg, setSnackopen, params);
        if (Array.isArray(response)) { // Check if response is an array
          const formattedFilters = response.reduce((acc, filter) => {
            acc[filter.filter_by] = {
              operator: filter.operator,
              value: filter.value,
            };
            return acc;
          }, {});
          setFilters(formattedFilters);
    
          // Extract and set excludeSolutions from the response
          const solutionFilter = response.find((filter) => filter.filter_by === "solution");
          if (solutionFilter && solutionFilter.value) {
            const solutions = solutionFilter.value.split(",").map(Number); 
            setExcludeSolutions(solutions);
          }
        } else {
          setFilters({});
          setExcludeSolutions([]);
        }
      };
    
      loadFilters();
    }, []);
  
 

  useEffect(() => {
    const loadRegions = async () => {
      const regionList = await fetchRegionsAPI(
        setSnackmsg,
        setLoading,
        setSnackopen,
        params
      );
      setRegionOptions(regionList);
      setStopRefresh(false)
    };

    loadRegions();
  }, []);

  useEffect(() => {
    const loadPlannerEINs = async () => {
      const einList = await fetchPlannerEinAPI(
        setSnackmsg,
        setLoading,
        setSnackopen,
        params
      );
      setPlannerEINOptions(einList);
    };

    loadPlannerEINs();
  }, []);

  useEffect(() => {
    const fetchEINOptions = async () => {
      const options = await fetchPlannerEinAPI(
        setSnackmsg,
        setLoading,
        setSnackopen,
        params
      );
      setPlannerEINOptions(options || []);
    };
    fetchEINOptions();
  }, []);

  useEffect(() => {
    const fetchRegionOption = async () => {
      const options = await fetchRegionsAPI(
        setSnackmsg,
        setLoading,
        setSnackopen,
        params
      );
      setRegionOptions(options || []);
    };
    fetchRegionOption();
  }, []);

  const handleSubmit = async () => {
    await setSubmit(setSnackopen, setSnackmsg, setLoading, plannerEIN, region,setSnackSuccess,setSuccessAlert);
  };

  const handleOperatorChange = (field, operator) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: { ...prevFilters[field], operator },
    }));
  };

 const handleValueChange = (field, value) => {
  // Convert value to number if it's a valid number
  const isValidNumber = /^-?\d+(\.\d{1,2})?$/.test(value);
  const numericValue = isValidNumber ? Number(value) : null;

  // Define min and max range
  const MIN_INT = -2147483648;
  const MAX_INT = 2147483647;

  if (field === "netCost") {
    // Allow negative values and check range
    if (
      value === "" ||
      (isValidNumber && numericValue >= MIN_INT && numericValue <= MAX_INT)
    ) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [field]: { ...prevFilters[field], value },
      }));
    }
  } else {
    // Default validation for other fields (positive numbers with up to 2 decimal places)
    const isValidDecimal = /^\d+(\.\d{1,2})?$/.test(value);
    const decimalValue = isValidDecimal ? Number(value) : null;

    if (
      value === "" ||
      (isValidDecimal && decimalValue >= 0 && decimalValue <= MAX_INT)
    ) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [field]: { ...prevFilters[field], value },
      }));
    }
  }
};


  const handleCheckboxChange = (solution) => {
    setExcludeSolutions((prev) =>
      prev.includes(solution)
        ? prev.filter((s) => s !== solution)
        : [...prev, solution]
    );
  };

  const handleApplyFilter = () => {
    setFilter(
      setSnackopen,
      setSnackmsg,
      setLoading,
      params,
      filters,
      excludeSolutions,
      setSnackSuccess,
      setSuccessAlert,
    );
    const checkedList = JSON.parse(localStorage.getItem("checkedList") || "[]");
 
// Check if the array exists and has at least one object
if (checkedList.length > 0) {
  // Update the selectedSolution property of the first object
  checkedList[0].selectedSolution = excludeSolutions;
 
  // Store the updated array back into localStorage
  localStorage.setItem("checkedList", JSON.stringify(checkedList));
} else {
  console.error("checkedList is empty or not found in localStorage.");
}    
  };

  const renderFilterRow = (label, field) => (
    <Grid container spacing={3} alignItems="center" sx={{ marginBottom: 2 }}>
      <Grid item xs={3}>
        <Typography sx={{ fontSize: 12, fontWeight: "bold" }}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={5}>
        <FormControl fullWidth size="small" sx={{ width: "100%" }}>
          <InputLabel id={`${field}-operator-label`} sx={{ fontSize: 12 }}>
            Operator
          </InputLabel>
          <Select
            labelId={`${field}-operator-label`}
            value={filters[field]?.operator || ""}
            onChange={(e) => handleOperatorChange(field, e.target.value)}
            sx={{ fontSize: 12 }}
          >
            <MenuItem value=">" sx={{ fontSize: 12 }}>
              Greater Than (&gt;)
            </MenuItem>
            <MenuItem value="<" sx={{ fontSize: 12 }}>
              Less Than (&lt;)
            </MenuItem>
            <MenuItem value="=" sx={{ fontSize: 12 }}>
              Equal To (=)
            </MenuItem>
            <MenuItem value=">=" sx={{ fontSize: 12 }}>
              Greater Than or Equal To (&ge;)
            </MenuItem>
            <MenuItem value="<=" sx={{ fontSize: 12 }}>
              Less Than or Equal To (&le;)
            </MenuItem>
            <MenuItem value="<>" sx={{ fontSize: 12 }}>
              Not Equal To (&ne;)
            </MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={3}>
        <TextField
          label="Value"
          value={filters[field]?.value || ""}
          onChange={(e) => handleValueChange(field, e.target.value)}
          size="small"
          type="number"
          fullWidth
          sx={{ width: "100px", fontSize: 12 }}
          InputProps={{
            inputProps: { min: 0 },
          }}
        />
      </Grid>
    </Grid>
  );

  return (
    
    <Box sx={{ display: "flex", gap: 2, marginTop: "10px" }}>
      <Card
        id="admin-filter-card"
        sx={{ height: "auto", marginTop: "10px", borderRadius: 0.5 }}
      >
        <CardHeader
          id="admin-filter-header"
          sx={{
            background: "linear-gradient(to bottom,  #142032 ,#2e3a4c)",
            padding: 0.5,
          }}
          titleTypographyProps={{
            fontSize: 16,
            fontWeight: "bold",
            color: "white",
          }}
          title="ADMIN FILTER"
        />
        <CardContent>
          <Box sx={{ width: "500px" }}>
            {/* Render filter rows */}
            {renderFilterRow("Cable Weight", "cable_weight")}
            {renderFilterRow("Divert / Tonne", "divertPerTone")}
            {renderFilterRow("Net Cost", "netCost")}
            {renderFilterRow("KG / m", "kgPerMeter")}
            {renderFilterRow("Tonne / Cab", "tonePerCab")}

            {/* Exclude Solution Section */}
            <Typography
              sx={{ fontSize: 12, fontWeight: "bold", marginBottom: 1 }}
            >
              Exclude Solution:
            </Typography>
            <Grid container spacing={1}>
              {[1, 2, 3, 4, 5].map((solution) => (
                <Grid item key={solution}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={excludeSolutions.includes(solution)}
                        onChange={() => handleCheckboxChange(solution)}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 12 }}>{solution}</Typography>
                    }
                  />
                </Grid>
              ))}
            </Grid>

            {/* Apply Filter Button */}
            <Grid container justifyContent="flex-start" sx={{ marginTop: 2 }}>
              <Grid item xs={3}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyFilter}
                  sx={{
                    background: "linear-gradient(to bottom,  #142032 ,#2e3a4c)",
                    color: "white",
                    fontSize: 12,
                  }}
                >
                  Apply Filter
                </Button>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      <Card
        id="planner-region-card"
        sx={{ height: "auto", marginTop: "10px", borderRadius: 0.5 }}
      >
        <CardHeader
          id="planner-region-header"
          sx={{
            background: "linear-gradient(to bottom,  #142032 ,#2e3a4c)",
            padding: 0.5,
          }}
          titleTypographyProps={{
            fontSize: 16,
            fontWeight: "bold",
            color: "white",
          }}
          title="PLANNER REGION MAPPING"
        />
        <CardContent>
          <Box sx={{ width: "500px" }}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              sx={{ marginBottom: 2 }}
            >
              <Grid item xs={3}>
                <Typography sx={{ fontSize: 12, fontWeight: "bold" }}>
                  Region
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="region-label" sx={{ fontSize: 12 }}>
                    Select Region
                  </InputLabel>
                  <Select
                    labelId="region-label"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    label="Select Region"
                    sx={{ fontSize: 12 }}
                  >
                    {(regionOptions || []).map((option) => (
                      <MenuItem
                        key={option}
                        value={option}
                        sx={{ fontSize: 12 }}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={2}
              alignItems="center"
              sx={{ marginBottom: 2 }}
            >
              <Grid item xs={3}>
                <Typography sx={{ fontSize: 12, fontWeight: "bold" }}>
                  Planner EIN
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="planner-ein-label" sx={{ fontSize: 12 }}>
                    Select EIN
                  </InputLabel>
                  <Select
                    labelId="planner-ein-label"
                    value={plannerEIN}
                    onChange={(e) => setPlannerEIN(e.target.value)}
                    label="Select EIN"
                    sx={{ fontSize: 12 }}
                  >
                    {(plannerEINOptions || []).map((option) => (
                      <MenuItem
                        key={option}
                        value={option}
                        sx={{ fontSize: 12 }}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container justifyContent="flex-start" sx={{ marginTop: 2 }}>
              <Grid item xs={3}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSubmit}
                  sx={{
                    background: "linear-gradient(to bottom,  #142032 ,#2e3a4c)",
                    color: "white",
                    fontSize: 12,
                  }}
                >
                  Submit
                </Button>
                 <Snackbar open={successAlert} autoHideDuration={6000} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                        <Alert onClose={handleClose} severity="success" sx={{ width: '100%' }}>
                          {snackSuccess}
                        </Alert>
                      </Snackbar>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminFilter;
